import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { Overlay } from '@/components/Overlay';
import { Glyph, ToastLine } from '@/components/Translating';
import { Link } from 'react-router-dom';
import { localeLabels, locales, type Locale } from '@sonasi/shared-types';

import { saveMessage } from '@/lib/apps';
import {
  useApplyTranslation,
  useDraftTranslation,
  usePlanTranslation,
  type ApplyMode,
  type TranslatableKind,
  type TranslationApplied,
  type TranslationLocaleDraft,
} from '@/lib/translation';

/**
 * "Translate with AI", as the design draws it: press, see what it would do,
 * start it, watch each language land, then say how to apply it.
 *
 * ⚠️ **The press only asks; Start translating spends; nothing is written
 * until the last button.** The modal opens on the plan — "36 fields across
 * three languages", every row queued, 0% — and waits. The design's second
 * revision added that wait: a paid call should not start from a button that
 * looks like it opens a dialog. **Start translating** runs it — a progress
 * bar, one row per language (✓ done · · translating · queued), a Stop button
 * — and the model's answers are held here, in the browser. When every
 * language is back, the title turns to "Translation ready" and the primary
 * button appears: **Fill empty fields**, or **Overwrite all** if the checkbox
 * is ticked. Only that press writes.
 *
 * ⚠️ **Overwrite is a checkbox, off by default, worded as the design words
 * it** — "including anything edited by hand". Anh Duy chốt "only fill what is
 * empty" first; the design then drew the choice, so it is here as an explicit
 * opt-in and never the default. Filling decides "empty" on the server at
 * write time, so a Japanese tagline typed while the modal was open is kept.
 *
 * ⚠️ **If the record is live, so is the translation.** Said before the press.
 */
/** What to call the record in a sentence: "This legal is live" is not English. */
const NOUN: Record<TranslatableKind, string> = {
  app: 'app',
  post: 'post',
  legal: 'document',
  site: 'site copy',
};

export function TranslateWithAi({
  kind,
  id,
  offered,
}: {
  kind: TranslatableKind;
  id: string;
  /**
   * Whether the button is shown — true on the base language only. The
   * component stays mounted on every tab regardless: unmounting it threw away
   * a run in progress the moment somebody switched to 日本語 to watch it land.
   */
  offered: boolean;
}) {
  const plan = usePlanTranslation();
  const draft = useDraftTranslation();
  const apply = useApplyTranslation();

  const [open, setOpen] = useState(false);
  const [landed, setLanded] = useState<TranslationLocaleDraft[]>([]);
  const [overwrite, setOverwrite] = useState(false);
  const [stopped, setStopped] = useState(false);
  const [toast, setToast] = useState<{ message: string; ok: boolean } | null>(null);
  const stopRef = useRef(false);
  const trigger = useRef<HTMLButtonElement>(null);

  const into = locales.filter((locale) => locale !== 'en');

  const [started, setStarted] = useState(false);
  const running = draft.isPending;
  const languages =
    plan.data?.locales.filter((l) => l.fill.length > 0).map((l) => l.locale as Locale) ?? [];
  const total = plan.data ? languages.length : into.length;

  const reset = () => {
    plan.reset();
    draft.reset();
    apply.reset();
    setLanded([]);
    setOverwrite(false);
    setStopped(false);
    setStarted(false);
    stopRef.current = false;
  };

  /*
   * The press: ask what would be sent (free — and it is where "not ready",
   * "live" and "too large" come from) and show it. Nothing is spent until
   * Start translating.
   */
  const openModal = () => {
    if (open) return;
    // Knowingly doubled: every path that closes the modal resets already, so
    // reverting this line changes nothing today. It is here for the close
    // path somebody adds next, which will not.
    reset();
    setOpen(true);
    plan.mutate({ kind, id, into });
  };

  /** Start translating: the call that costs. One language per request. */
  const start = () => {
    // `started` is knowingly doubled: the button unmounts on the render that
    // sets it, so a second press cannot reach here today. It guards the day
    // Start is wired to a key as well.
    if (started || languages.length === 0) return;
    setStarted(true);
    draft.mutate({
      kind,
      id,
      into: languages,
      onLanguage: setLanded,
      shouldStop: () => stopRef.current,
    });
  };

  const close = () => {
    setOpen(false);
    reset();
    trigger.current?.focus();
  };

  /** Stop: let the language in flight finish, ask for no more. */
  const stop = () => {
    stopRef.current = true;
    setStopped(true);
  };

  const commit = () => {
    const mode: ApplyMode = overwrite ? 'overwrite' : 'fill';
    apply.mutate(
      { kind, id, drafts: landed, mode },
      {
        onSuccess: (results) => {
          setOpen(false);
          reset();
          setToast({ message: summary(results, mode), ok: true });
          trigger.current?.focus();
        },
      },
    );
  };

  // The toast the design shows after applying; it clears itself.
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), toast.ok ? 3200 : 6000);
    return () => clearTimeout(timer);
  }, [toast]);

  // Escape closes — but not out of a run: Stop is the way out of that.
  useEffect(() => {
    if (!open) return;
    const key = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !draft.isPending && !apply.isPending) close();
    };
    document.addEventListener('keydown', key);
    return () => document.removeEventListener('keydown', key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, draft.isPending, apply.isPending]);

  const done = open && started && !running;
  const drafted = landed.filter((d) => !d.failed && Object.keys(d.blocks).length > 0);

  return (
    <>
      {offered ? (
        <button
          ref={trigger}
          type="button"
          onClick={openModal}
          /*
            Not disabled while the dialog is merely open: `close()` hands the
            keyboard back to this button in the same tick, before the
            re-render that would enable it, and a disabled button refuses
            focus. The backdrop keeps the pointer off it; `openModal` ignores
            a press while open.
          */
          disabled={running}
          className="btn-small focus-ring inline-flex cursor-pointer items-center gap-1.5 font-medium whitespace-nowrap disabled:cursor-default disabled:opacity-50"
          style={
            running
              ? {
                  background: 'var(--accent-soft)',
                  color: 'var(--accent)',
                  borderColor: 'var(--accent)',
                }
              : undefined
          }
        >
          <Glyph spinning={running} />
          {running ? 'Translating…' : 'Translate with AI'}
        </button>
      ) : null}

      {/*
        ⚠️ **Portalled to `<body>`, and it is not optional.** This button lives
        on the language bar, which is `sticky` with a `backdrop-filter` — and
        a backdrop-filter makes its element the containing block for every
        `position: fixed` descendant. Rendered in place, `inset-0` meant "the
        bar": the backdrop was 534×84px, the dialog's top sat 75px above the
        viewport with the title and the progress bar in it, and the form behind
        was not covered, so Save could be pressed mid-run. jsdom lays nothing
        out and could not see any of it; Em Linh's browser could.
      */}
      {open
        ? createPortal(
            <TranslateModal
              kind={kind}
              checking={plan.isPending}
              started={started}
              running={running}
              done={done}
              stopped={stopped}
              plan={plan}
              landed={landed}
              total={total}
              overwrite={overwrite}
              onOverwrite={setOverwrite}
              applying={apply.isPending}
              applyError={apply.error ? apply.error.message : null}
              canApply={drafted.length > 0}
              onStart={start}
              onStop={stop}
              onClose={close}
              onApply={commit}
            />,
            document.body,
          )
        : null}

      {/* Same reason as the modal: `fixed` inside the bar is fixed *to* the bar. */}
      {toast
        ? createPortal(<ToastLine message={toast.message} ok={toast.ok} />, document.body)
        : null}
    </>
  );
}

/* ────────────────────────────── the modal ────────────────────────────── */

function TranslateModal({
  kind,
  checking,
  started,
  running,
  done,
  stopped,
  plan,
  landed,
  total,
  overwrite,
  onOverwrite,
  applying,
  applyError,
  canApply,
  onStart,
  onStop,
  onClose,
  onApply,
}: {
  kind: TranslatableKind;
  /** The plan is still being fetched. */
  checking: boolean;
  /** Start translating has been pressed. */
  started: boolean;
  /** A language is being translated right now. */
  running: boolean;
  done: boolean;
  stopped: boolean;
  plan: ReturnType<typeof usePlanTranslation>;
  landed: TranslationLocaleDraft[];
  total: number;
  overwrite: boolean;
  onOverwrite: (on: boolean) => void;
  applying: boolean;
  applyError: string | null;
  canApply: boolean;
  onStart: () => void;
  onStop: () => void;
  onClose: () => void;
  onApply: () => void;
}) {
  const titleId = useId();
  const primary = useRef<HTMLButtonElement>(null);
  const startButton = useRef<HTMLButtonElement>(null);
  const stopButton = useRef<HTMLButtonElement>(null);
  const cancelButton = useRef<HTMLButtonElement>(null);
  const dialog = useRef<HTMLDivElement>(null);

  const p = plan.data;
  /*
   * A plan that could not be fetched is a "not ready" of its own. Left to the
   * default branch it read "Nothing came back · Every language failed" over
   * a 0% bar — a verdict on languages that were never asked — the day the
   * running API was a build behind the admin. The server's message is the
   * alert below; the subtitle says what state that leaves things in.
   */
  const notReady = plan.isError
    ? 'Could not check what there is to translate — nothing was sent.'
    : p && !p.ready
      ? p.notReady
      : p?.tooLarge
        ? 'That is over the limit for one run — translate fewer languages at a time.'
        : null;
  const nothing = p?.ready && !p.tooLarge && p.locales.every((l) => l.fill.length === 0);
  /*
   * ⚠️ One word, one unit, one number. "Fields" here, in each language's row
   * and in the toast is the same count of blocks — a paragraph, a list of
   * features — that `apply` reports back as written or kept. A first version
   * showed the sum over every language in the subtitle, the count of strings
   * the model was sent in the rows, and the blocks written in the toast: three
   * numbers wearing one word. Every language is sent the same blocks, so the
   * first language's list is the run's.
   */
  const fields = p?.locales[0]?.fill.length ?? 0;
  const languages = p?.locales.filter((l) => l.fill.length > 0).length ?? 0;
  const percent = total === 0 ? 0 : Math.round((landed.length / total) * 100);
  /** The plan is in and there is something in it to start. */
  const canStart = !!p && !checking && !notReady && !nothing && !started;

  /*
   * ⚠️ Somewhere in the dialog holds the keyboard at every step. The button
   * that opened it is disabled while the dialog is up, so focus fell to
   * `<body>` and the next Tab walked the sidebar and the form *behind* the
   * backdrop — Enter on the Save it reached there saved mid-run. There is no
   * focus trap in this admin yet; the one thing to do at each step is where
   * the keyboard goes: Start, then Stop, then the button that writes — and
   * Cancel while there is nothing else.
   */
  useEffect(() => {
    const next =
      done && canApply ? primary : running ? stopButton : canStart ? startButton : cancelButton;
    /*
     * ⚠️ "Stopping…" has no button left to hold: Stop turns into a disabled
     * Cancel, and Chrome and Safari blur a focused element the moment it is
     * disabled — the keyboard fell to `<body>` for as long as the language
     * in flight took, Tab away from the form behind the backdrop by nothing.
     * jsdom does not blur it, which is how a first version shipped without
     * this line and with a green test. The dialog itself takes the focus.
     */
    (next.current ?? dialog.current)?.focus();
  }, [done, canApply, running, canStart, stopped]);

  const title = notReady
    ? 'Cannot translate yet'
    : nothing
      ? 'Nothing to translate'
      : !started
        ? 'Translate with AI'
        : running
          ? 'Translating with AI'
          : canApply
            ? 'Translation ready'
            : 'Nothing came back';
  const subtitle = notReady
    ? notReady
    : nothing
      ? 'No block has any English to translate from.'
      : !started
        ? checking
          ? 'Checking what there is to translate…'
          : `${fields * languages} fields across ${counted(languages, 'language')}`
        : running
          ? `Reading the English copy · ${fields} ${fields === 1 ? 'field' : 'fields'}`
          : stopped
            ? canApply
              ? 'Stopped — the languages that came back can still be applied'
              : 'Stopped — nothing came back that can be applied'
            : canApply
              ? 'Review each language before publishing'
              : 'Every language failed — the reasons are on each row';

  const rows = (p?.locales ?? []).map((l, index) => {
    const result = landed.find((d) => d.locale === l.locale);
    const active = running && !result && landed.length === index;
    return { locale: l.locale, result, active, queued: !result && !active };
  });

  return (
    // The shared sheet — dark in both themes. `var(--bg)` at 72% was white
    // on the light theme. Outside is not a way out of a run.
    <Overlay z={90} onBackdropClick={running || applying ? undefined : onClose}>
      <div
        ref={dialog}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        // `outline-none`: the dialog takes focus only while "Stopping…", and a ring around the whole box reads as a bug.
        className="flex w-[min(440px,100%)] flex-col gap-3.5 rounded-[14px] border p-[18px] outline-none"
        style={{
          background: 'var(--panel)',
          borderColor: 'var(--line2)',
          boxShadow: '0 30px 70px -30px rgba(0, 0, 0, 0.7)',
        }}
      >
        <span className="flex items-center gap-2.5">
          <span
            aria-hidden="true"
            className="flex h-[30px] w-[30px] flex-none items-center justify-center rounded-[9px]"
            style={{ background: 'var(--accent-soft)', color: 'var(--accent)' }}
          >
            <Glyph spinning={running} size={16} />
          </span>
          <span className="flex min-w-0 flex-col gap-0.5">
            <span
              id={titleId}
              className="text-[13.5px] font-semibold tracking-[-0.01em]"
              style={{ color: 'var(--text)' }}
            >
              {title}
            </span>
            <span className="text-[11.5px]" style={{ color: 'var(--muted)' }}>
              {subtitle}
            </span>
          </span>
          {!notReady && !nothing ? (
            <span
              className="ml-auto font-mono text-[11px]"
              style={{ color: 'var(--accent)' }}
              aria-live="polite"
            >
              {percent}%
            </span>
          ) : null}
        </span>

        {!notReady && !nothing ? (
          <>
            <span
              className="block h-[3px] overflow-hidden rounded-full"
              style={{ background: 'var(--panel2)' }}
            >
              <span
                role="progressbar"
                aria-label="Translation progress"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={percent}
                className="block h-full rounded-full transition-[width] duration-[450ms]"
                style={{ width: `${percent}%`, background: 'var(--accent)' }}
              />
            </span>

            <ul className="m-0 flex list-none flex-col gap-px p-0">
              {rows.map(({ locale, result, active, queued }) => (
                <li
                  key={locale}
                  className="flex items-center gap-2.5 border-b px-0.5 py-[7px]"
                  style={{ borderColor: 'var(--line)' }}
                >
                  <span
                    className="w-3.5 text-center text-[11px]"
                    style={{
                      color: result?.failed
                        ? 'var(--danger)'
                        : result
                          ? 'var(--ok)'
                          : 'var(--accent)',
                    }}
                    aria-hidden="true"
                  >
                    {result?.failed ? '×' : result ? '✓' : active ? '·' : ''}
                  </span>
                  <span
                    className="flex-none text-[12.5px]"
                    style={{ color: result || active ? 'var(--text)' : 'var(--dim)' }}
                  >
                    {localeLabels[locale as Locale] ?? locale} · {languageName(locale)}
                  </span>
                  {/*
                    The reason is the part that gives: a provider's error can
                    run to a sentence, and unwrapped it squeezed the language's
                    name onto three lines instead.
                  */}
                  <span
                    className="ml-auto min-w-0 text-right font-mono text-[10.5px] break-words"
                    style={{ color: result?.failed ? 'var(--danger)' : 'var(--dim)' }}
                  >
                    {result?.failed
                      ? result.failed
                      : result
                        ? `${result.fields} ${result.fields === 1 ? 'field' : 'fields'}`
                        : active
                          ? 'translating…'
                          : queued && stopped
                            ? 'stopped'
                            : 'queued'}
                  </span>
                </li>
              ))}
            </ul>

            {p?.published ? (
              <span
                className="flex items-start gap-2 text-[11.5px]"
                style={{ color: 'var(--warn)' }}
              >
                <span
                  aria-hidden="true"
                  className="mt-[5px] h-[5px] w-[5px] flex-none rounded-full"
                  style={{ background: 'currentColor' }}
                />
                <span>
                  This {NOUN[kind]} is live — whatever is applied goes on the website at once.
                </span>
              </span>
            ) : null}

            {/* A choice about how to apply, shown only while there is something to apply. */}
            {!started || running || canApply ? (
              <label
                className="flex items-start gap-2.5 rounded-[9px] border px-3 py-2.5"
                style={{
                  background: 'var(--in)',
                  borderColor: overwrite ? 'var(--accent)' : 'var(--line)',
                  opacity: running ? 0.55 : 1,
                  cursor: running ? 'default' : 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={overwrite}
                  disabled={running || applying}
                  onChange={(event) => onOverwrite(event.target.checked)}
                  className="mt-0.5 h-3.5 w-3.5"
                  style={{ accentColor: 'var(--accent)' }}
                />
                <span className="flex min-w-0 flex-col gap-0.5">
                  <span className="text-[12.5px] font-semibold" style={{ color: 'var(--text)' }}>
                    Overwrite every field
                  </span>
                  <span className="text-[11.5px] leading-[1.5]" style={{ color: 'var(--muted)' }}>
                    {overwrite
                      ? `Replaces existing ${listed((p?.locales ?? []).map((l) => l.locale))} copy, including anything edited by hand.`
                      : 'Leaves existing copy untouched and fills only the empty fields.'}
                  </span>
                </span>
              </label>
            ) : null}
          </>
        ) : null}

        {notReady && p && !p.ready ? (
          <Link
            to="/settings/connections"
            className="prose-link focus-ring self-start text-[12px]"
            style={{ color: 'var(--accent)' }}
          >
            Settings › Connections
          </Link>
        ) : null}

        {applyError ? (
          <span role="alert" className="text-[11.5px]" style={{ color: 'var(--danger)' }}>
            {applyError}
          </span>
        ) : null}
        {plan.isError ? (
          <span role="alert" className="text-[11.5px]" style={{ color: 'var(--danger)' }}>
            {saveMessage(plan.error)}
          </span>
        ) : null}

        <span className="flex justify-end gap-2">
          {running && !stopped ? (
            <button
              ref={stopButton}
              type="button"
              onClick={onStop}
              className="btn-outline focus-ring min-h-8 cursor-pointer px-[13px] py-[7px] text-[12.5px]"
            >
              Stop
            </button>
          ) : (
            <button
              ref={cancelButton}
              type="button"
              onClick={onClose}
              disabled={running || applying}
              className="btn-outline focus-ring min-h-8 cursor-pointer px-[13px] py-[7px] text-[12.5px] disabled:cursor-default disabled:opacity-50"
            >
              {running ? 'Stopping…' : 'Cancel'}
            </button>
          )}
          {canStart ? (
            <button
              ref={startButton}
              type="button"
              onClick={onStart}
              className="btn-accent focus-ring min-h-8 cursor-pointer px-[13px] py-[7px] text-[12.5px]"
            >
              Start translating
            </button>
          ) : null}
          {done && canApply ? (
            <button
              ref={primary}
              type="button"
              onClick={onApply}
              disabled={applying}
              className="btn-accent focus-ring min-h-8 cursor-pointer px-[13px] py-[7px] text-[12.5px] disabled:cursor-default disabled:opacity-50"
            >
              {applying ? 'Applying…' : overwrite ? 'Overwrite all' : 'Fill empty fields'}
            </button>
          ) : null}
        </span>
      </div>
    </Overlay>
  );
}

/* ─────────────────────────────── bits ─────────────────────────────── */

const LANGUAGE_NAMES: Record<string, string> = {
  ja: 'Japanese',
  ko: 'Korean',
  zh: 'Simplified Chinese',
};
const languageName = (locale: string): string => LANGUAGE_NAMES[locale] ?? locale;

/** "three languages", "one language" — the design writes the small numbers out. */
const counted = (n: number, noun: string): string => {
  const words = ['no', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine'];
  return `${words[n] ?? n} ${noun}${n === 1 ? '' : 's'}`;
};

/** "日本語, 한국어 and 中文" — from the languages the plan named, not a list typed here. */
const listed = (codes: string[]): string => {
  const names = codes.map((code) => localeLabels[code as Locale] ?? code);
  if (names.length <= 1) return names.join('');
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
};

/** The toast's one sentence, in the design's words. */
const summary = (results: TranslationApplied[], mode: ApplyMode): string => {
  const written = results.reduce((sum, r) => sum + r.written, 0);
  const kept = results.reduce((sum, r) => sum + r.kept, 0);
  if (written === 0) return 'Nothing was written — every field already had text.';
  return mode === 'overwrite'
    ? `${written} ${written === 1 ? 'field' : 'fields'} overwritten · review before publishing`
    : `${written} empty ${written === 1 ? 'field' : 'fields'} filled${kept > 0 ? ` · ${kept} existing kept` : ''}`;
};
